import React, { useRef, useState } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import axios from 'axios'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`
const BF = 'Anton, sans-serif'
const MONO = 'JetBrains Mono, monospace'
const SG = 'Space Grotesk, sans-serif'

function FadeIn({ children, delay = 0, className = '' }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })
  return (
    <motion.div ref={ref} className={className}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
      {children}
    </motion.div>
  )
}

function RevealLine({ children, delay = 0 }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })
  return (
    // Extra paddingTop/Bottom prevents "¿" and "?" from being clipped
    <div ref={ref} style={{ overflow: 'hidden', paddingTop: '0.15em', marginTop: '-0.15em', paddingBottom: '0.1em', marginBottom: '-0.1em' }}>
      <motion.div
        initial={{ y: '105%' }}
        animate={inView ? { y: 0 } : {}}
        transition={{ delay, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}>
        {children}
      </motion.div>
    </div>
  )
}

export default function Auditoria() {
  const [url, setUrl] = useState('')
  const [status, setStatus] = useState('idle')
  const [results, setResults] = useState([])
  const [errorMsg, setErrorMsg] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!url.trim()) return
    setStatus('loading')
    setResults([])
    setErrorMsg('')
    try {
      const res = await axios.post(`${API}/audit`, { url: url.trim() }, { timeout: 32000 })
      setResults(res.data.improvements || [])
      setStatus('done')
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || 'No se pudo analizar la web. Comprueba la URL e inténtalo de nuevo.')
      setStatus('error')
    }
  }

  const handleReset = () => { setStatus('idle'); setResults([]); setUrl(''); setErrorMsg('') }

  return (
    <section id="auditoria" data-testid="auditoria-section"
      className="bg-black py-24 md:py-36 border-t border-neutral-900">
      <div className="max-w-screen-xl mx-auto px-6 md:px-12 xl:px-16">

        <FadeIn delay={0}>
          <div className="flex items-center gap-4 mb-14">
            <span className="accent-rule" />
            <span className="text-xs tracking-[0.35em] text-amber-400 uppercase" style={{ fontFamily: MONO }}>
              Auditoría Express
            </span>
          </div>
        </FadeIn>

        {/* Heading centered — with anti-clipping padding */}
        <div className="text-center mb-12">
          <RevealLine delay={0.1}>
            <h2 className="uppercase text-white leading-none"
              style={{ fontFamily: BF, fontSize: 'clamp(48px, 8vw, 120px)', letterSpacing: '0.01em' }}>
              ¿CÓMO ESTÁ
            </h2>
          </RevealLine>
          <RevealLine delay={0.22}>
            <h2 className="uppercase leading-none"
              style={{ fontFamily: BF, fontSize: 'clamp(48px, 8vw, 120px)', letterSpacing: '0.01em', color: '#F5A623' }}>
              TU WEB?
            </h2>
          </RevealLine>
        </div>

        <FadeIn delay={0.35}>
          <p className="text-white/50 text-center mb-14 max-w-lg mx-auto leading-relaxed" style={{ fontFamily: SG, fontSize: '16px' }}>
            Introduce la URL de tu web y la IA analizará en segundos los 3 puntos más importantes que podrías mejorar.
          </p>
        </FadeIn>

        {/* URL input */}
        {status !== 'done' && (
          <FadeIn delay={0.4}>
            <form onSubmit={handleSubmit} data-testid="audit-form"
              className="flex flex-col sm:flex-row gap-4 max-w-2xl mx-auto mb-10">
              <input type="url" value={url} onChange={(e) => setUrl(e.target.value)}
                placeholder="https://tu-web.com" required data-testid="audit-url-input"
                className="flex-1 bg-transparent border-b border-neutral-700 focus:border-amber-400 pb-3 text-white placeholder-white/25 focus:outline-none transition-colors duration-300 text-center sm:text-left"
                style={{ fontFamily: SG, fontSize: '17px' }}
                disabled={status === 'loading'} />
              <motion.button type="submit" disabled={status === 'loading'}
                data-testid="audit-submit-button"
                whileHover={{ x: 2 }} whileTap={{ scale: 0.97 }}
                className="flex items-center justify-center gap-3 text-amber-400 hover:text-white transition-colors duration-300 disabled:opacity-40 bg-transparent border-b border-amber-400 pb-3"
                style={{ fontFamily: MONO, fontSize: '12px', letterSpacing: '0.25em', whiteSpace: 'nowrap' }}>
                {status === 'loading' ? (
                  <>
                    <motion.span animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>◌</motion.span>
                    <span className="uppercase">Analizando...</span>
                  </>
                ) : (
                  <><span className="uppercase">Analizar</span><span className="text-lg">↗</span></>
                )}
              </motion.button>
            </form>

            {status === 'error' && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="text-red-400 text-center text-xs tracking-wider mb-6"
                style={{ fontFamily: MONO }} data-testid="audit-error-message">
                {errorMsg}
              </motion.p>
            )}
          </FadeIn>
        )}

        {/* Results */}
        <AnimatePresence>
          {status === 'done' && results.length > 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} data-testid="audit-results">
              <div className="flex items-center justify-center gap-3 mb-12">
                <div className="w-1.5 h-1.5 bg-amber-400" />
                <span className="text-amber-400 text-xs tracking-[0.2em] uppercase" style={{ fontFamily: MONO }}>
                  Análisis completado — {url}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-neutral-900">
                {results.map((item, i) => (
                  <motion.div key={i} data-testid={`audit-result-${i + 1}`}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="border-l border-neutral-900 px-7 py-8 first:border-l-0 md:first:border-l">
                    <div className="text-amber-400 mb-5 text-4xl" style={{ fontFamily: BF, letterSpacing: '0.01em' }}>
                      0{i + 1}
                    </div>
                    <h3 className="text-white uppercase mb-4 leading-tight"
                      style={{ fontFamily: BF, fontSize: 'clamp(18px, 1.6vw, 24px)', letterSpacing: '0.01em' }}>
                      {item.titulo}
                    </h3>
                    <p className="text-white/45 mb-5 leading-relaxed" style={{ fontFamily: SG, fontSize: '14px' }}>
                      {item.problema}
                    </p>
                    <div className="border-t border-neutral-900 pt-5">
                      <span className="text-white/25 text-xs tracking-[0.2em] uppercase block mb-2" style={{ fontFamily: MONO }}>
                        Solución recomendada
                      </span>
                      <p className="text-amber-400/80 leading-relaxed" style={{ fontFamily: SG, fontSize: '13px' }}>
                        {item.solucion}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }} className="text-center mt-14">
                <p className="text-white/40 mb-6" style={{ fontFamily: SG, fontSize: '15px' }}>
                  ¿Quieres que arregle estos problemas? Hablamos sin compromiso.
                </p>
                <div className="flex items-center justify-center gap-6 flex-wrap">
                  <a href="#contacto"
                    onClick={(e) => { e.preventDefault(); document.querySelector('#contacto')?.scrollIntoView({ behavior: 'smooth' }) }}
                    className="text-amber-400 hover:text-white transition-colors duration-300 text-xs tracking-[0.25em] uppercase"
                    style={{ fontFamily: MONO }}>
                    Contactar ↗
                  </a>
                  <button onClick={handleReset} data-testid="audit-reset-button"
                    className="text-white/30 hover:text-white transition-colors duration-300 text-xs tracking-[0.25em] uppercase bg-transparent border-0">
                    Analizar otra web →
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  )
}
